import { css } from "@compiled/react";
import { IntlShape, MessageDescriptor, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";

import { Adg4DraggableTab } from "@/components/adg4/tabs/src/draggable-tab";
import { Adg4DraggableTabList } from "@/components/adg4/tabs/src/draggable-tab-list";
import { Adg4TabPanel } from "@/components/adg4/tabs/src/tab-panel";
import { Adg4Tabs, useAdg4TabsContext } from "@/components/adg4/tabs/src/tabs";
import { RoundTab } from "@/components/confluence/product-tour/src/round-tab";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import {
    ListContentCycle,
    ListContentItem
} from "@/patterns/list-content-cycle/list-content-cycle";
import { withScrollTracking } from "@/utils/scroll-tracking/scroll-tracking";

import {
    templatesSectionBusinessStrategyMessages,
    templatesSectionMarketingMessages,
    templatesSectionMessages,
    templatesSectionProductManagementMessages,
    templatesSectionProjectPlanningMessages,
    templatesSectionSoftwareMessages
} from "./messages/templates-section.messages";

const tabsWrapper = css({
    position: "relative",
    zIndex: 1,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".tab-list"]: {
        gap: 12,
        paddingBlock: 40,
        paddingInline: 16
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".round-tab"]: {
        borderRadius: 28
    }
});

const headingStyle = css({
    textAlign: "center",
    marginBlockEnd: 0
});

const linkStyle = css({
    [media.below.md]: {
        marginBlock: 32
    },
    [media.above.md]: {
        marginBlockEnd: 64
    }
});

// Prevent height jump when image is loading
const contentHeight = css({
    [media.above.md]: {
        minHeight: 464
    }
});

type TemplateCategory = {
    tabName: string;
    categoryTitle: string;
    eventProps: {
        label: string;
        labelEnglish: string;
    };
    items: ListContentItem[];
};

function item(
    intl: IntlShape,
    title: MessageDescriptor,
    description: MessageDescriptor,
    imgSrc: string,
    templateLinkPrefix: string,
    templateSlug: string,
    lazyLoad = true
): ListContentItem {
    const titleText = intl.formatMessage(title);
    const headingId = `${templateSlug}-heading`;
    const linkId = `${templateSlug}-link`;
    return {
        title: titleText,
        description: intl.formatMessage(description),
        expandAriaLabel: intl.formatMessage(
            templatesSectionMessages.expandAriaLabel,
            {
                title: titleText
            }
        ),
        content: (
            // eslint-disable-next-line no-restricted-syntax
            <img
                alt=""
                loading={lazyLoad ? "lazy" : undefined}
                src={imgSrc}
                width="100%"
            />
        ),
        link: (
            // eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name
            <Link
                text={intl.formatMessage(templatesSectionMessages.tryItOut)}
                href={templateLinkPrefix + "/" + templateSlug}
                dataEventProps={{
                    "data-product-key": "confluence",
                    "data-event": "clicked",
                    "data-event-component": "linkButton",
                    "data-event-container": "templatesSection",
                    "data-label": templateSlug,
                    "data-label-english": "Try it out",
                    "data-track-event": "confluence-pt.templates.try-it-out"
                }}
                id={linkId}
                ariaLabelledby={`${headingId} ${linkId}`}
            />
        ),
        dataEventProps: {
            "data-product-key": "confluence",
            "data-event": "clicked",
            "data-event-component": "carouselItem",
            "data-event-container": "templatesSection",
            "data-label": templateSlug,
            "data-label-english":
                typeof title.defaultMessage === "string"
                    ? title.defaultMessage
                    : undefined,
            "data-track-event": "confluence-pt.templates.list-item"
        },
        headingId
    };
}

const TabContent = ({ category }: { category: TemplateCategory }) => {
    const { goToNextTab, userSelected } = useAdg4TabsContext();
    return (
        <ListContentCycle
            items={category.items}
            listHeading={category.categoryTitle}
            onCycleEnd={() => {
                if (!userSelected) {
                    goToNextTab();
                }
            }}
        />
    );
};

const TemplatesSection = () => {
    const intl = useIntl();
    const templatesLink = useLocalizedHref("/software/confluence/templates");

    const templateCategories: TemplateCategory[] = [
        {
            tabName: intl.formatMessage(
                templatesSectionMarketingMessages.tabName
            ),
            categoryTitle: intl.formatMessage(
                templatesSectionMarketingMessages.contentHeading
            ),
            eventProps: {
                label: "marketing-and-sales",
                labelEnglish: "Marketing"
            },
            items: [
                item(
                    intl,
                    templatesSectionMarketingMessages.template1Heading,
                    templatesSectionMarketingMessages.template1Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/marketing-template-01-plan.webp",
                    templatesLink,
                    "marketing-plan"
                ),
                item(
                    intl,
                    templatesSectionMarketingMessages.template2Heading,
                    templatesSectionMarketingMessages.template2Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/marketing-template-02-competitive-analysis.webp",
                    templatesLink,
                    "competitive-analysis"
                ),
                item(
                    intl,
                    templatesSectionMarketingMessages.template3Heading,
                    templatesSectionMarketingMessages.template3Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/marketing-template-03-campaign.webp",
                    templatesLink,
                    "marketing-campaign"
                )
            ]
        },
        {
            tabName: intl.formatMessage(
                templatesSectionProjectPlanningMessages.tabName
            ),
            categoryTitle: intl.formatMessage(
                templatesSectionProjectPlanningMessages.contentHeading
            ),
            eventProps: {
                label: "project-planning",
                labelEnglish: "Project Planning"
            },
            items: [
                item(
                    intl,
                    templatesSectionProjectPlanningMessages.template1Heading,
                    templatesSectionProjectPlanningMessages.template1Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/project-planning-template-01-brainstorm.webp",
                    templatesLink,
                    "brainstorming"
                ),
                item(
                    intl,
                    templatesSectionProjectPlanningMessages.template2Heading,
                    templatesSectionProjectPlanningMessages.template2Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/project-planning-template-02-plan.webp",
                    templatesLink,
                    "project-plan"
                ),
                item(
                    intl,
                    templatesSectionProjectPlanningMessages.template3Heading,
                    templatesSectionProjectPlanningMessages.template3Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/project-planning-template-03-roles.webp",
                    templatesLink,
                    "roles-and-responsibilities"
                )
            ]
        },
        {
            tabName: intl.formatMessage(
                templatesSectionSoftwareMessages.tabName
            ),
            categoryTitle: intl.formatMessage(
                templatesSectionSoftwareMessages.contentHeading
            ),
            eventProps: {
                label: "software-development",
                labelEnglish: "Software"
            },
            items: [
                item(
                    intl,
                    templatesSectionSoftwareMessages.template1Heading,
                    templatesSectionSoftwareMessages.template1Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/software-template-01-sprint-planning.webp",
                    templatesLink,
                    "sprint-planning-meeting"
                ),
                item(
                    intl,
                    templatesSectionSoftwareMessages.template2Heading,
                    templatesSectionSoftwareMessages.template2Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/software-template-02-experiment-plan.webp",
                    templatesLink,
                    "experiment-plan-and-results"
                ),
                item(
                    intl,
                    templatesSectionSoftwareMessages.template3Heading,
                    templatesSectionSoftwareMessages.template3Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/software-template-03-devops-runbook.webp",
                    templatesLink,
                    "devops-runbook"
                )
            ]
        },
        {
            tabName: intl.formatMessage(
                templatesSectionProductManagementMessages.tabName
            ),
            categoryTitle: intl.formatMessage(
                templatesSectionProductManagementMessages.contentHeading
            ),
            eventProps: {
                label: "product-management",
                labelEnglish: "Product Management"
            },
            items: [
                item(
                    intl,
                    templatesSectionProductManagementMessages.template1Heading,
                    templatesSectionProductManagementMessages.template1Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/product-template-01-requirements.webp",
                    templatesLink,
                    "product-requirements"
                ),
                item(
                    intl,
                    templatesSectionProductManagementMessages.template2Heading,
                    templatesSectionProductManagementMessages.template2Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/product-template-02-roadmap.webp",
                    templatesLink,
                    "product-roadmap"
                ),
                item(
                    intl,
                    templatesSectionProductManagementMessages.template3Heading,
                    templatesSectionProductManagementMessages.template3Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/product-template-03-launch.webp",
                    templatesLink,
                    "product-launch"
                )
            ]
        },
        {
            tabName: intl.formatMessage(
                templatesSectionBusinessStrategyMessages.tabName
            ),
            categoryTitle: intl.formatMessage(
                templatesSectionBusinessStrategyMessages.contentHeading
            ),
            eventProps: {
                label: "business-strategy",
                labelEnglish: "Business Strategy"
            },
            items: [
                item(
                    intl,
                    templatesSectionBusinessStrategyMessages.template1Heading,
                    templatesSectionBusinessStrategyMessages.template1Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/bus-strategy-template-01-vision.webp",
                    templatesLink,
                    "vision-to-values"
                ),
                item(
                    intl,
                    templatesSectionBusinessStrategyMessages.template2Heading,
                    templatesSectionBusinessStrategyMessages.template2Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/bus-strategy-template-02-strategic-plan.webp",
                    templatesLink,
                    "strategic-plan"
                ),
                item(
                    intl,
                    templatesSectionBusinessStrategyMessages.template3Heading,
                    templatesSectionBusinessStrategyMessages.template3Description,
                    "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/pt-templates/bus-strategy-template-03-updates.webp",
                    templatesLink,
                    "business-status-update"
                )
            ]
        }
    ];

    return (
        <UFOSegment name="confluence-pt-cfind-1234-templatesSection">
            <div css={tabsWrapper}>
                <Row>
                    <h2 css={headingStyle}>
                        {intl.formatMessage(templatesSectionMessages.heading)}
                    </h2>
                </Row>
                <Adg4Tabs>
                    <Adg4DraggableTabList className="tab-list">
                        {templateCategories.map((category, index) => {
                            return (
                                <Adg4DraggableTab
                                    key={index}
                                    className="round-tab"
                                    dataEventProps={{
                                        "data-product-key": "confluence",
                                        "data-event": "clicked",
                                        "data-event-component": "tab",
                                        "data-event-container":
                                            "templatesSection",
                                        "data-label": category.eventProps.label,
                                        "data-label-english":
                                            category.eventProps.labelEnglish,
                                        "data-track-event":
                                            "confluence-pt.templates.tab"
                                    }}
                                >
                                    <RoundTab title={category.tabName} />
                                </Adg4DraggableTab>
                            );
                        })}
                    </Adg4DraggableTabList>
                    {templateCategories.map((category, index) => {
                        return (
                            <Adg4TabPanel key={index}>
                                <div css={contentHeight}>
                                    <TabContent category={category} />
                                </div>
                            </Adg4TabPanel>
                        );
                    })}
                </Adg4Tabs>
                <Row>
                    <div css={linkStyle}>
                        {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
                        <Link
                            text={intl.formatMessage(
                                templatesSectionMessages.seeAllTemplates
                            )}
                            href={templatesLink}
                            dataEventProps={{
                                "data-product-key": "confluence",
                                "data-event": "clicked",
                                "data-event-component": "linkButton",
                                "data-event-container": "templatesSection",
                                "data-label": "see-all-templates",
                                "data-label-english": "See all templates",
                                "data-track-event":
                                    "confluence-pt.templates.see-all-templates"
                            }}
                        />
                    </div>
                </Row>
            </div>
        </UFOSegment>
    );
};

export default withScrollTracking("TemplatesSection", TemplatesSection, {
    observerOptions: {
        threshold: 0.1
    }
});
